import { useMemo } from 'react';

import { Field, Widget } from '@kanda-libs/ks-component-ts';
import { Text } from '@kanda-libs/ks-design-library';

import {
  EMPLOYED_PROPS,
  EMPLOYMENT_TYPE_OPTIONS,
  EMPLOYMENT_TYPE_VALIDATION,
} from './constants';

import { ERROR_PROPS } from '../../PrimaryEmployment/constants';
import useEmploymentStatusFields from '../../PrimaryEmployment/useEmploymentStatusFields';
import useMonthlyIncomeValidation from '../../useMonthlyIncomeValidation';
import EmploymentAddressFields from '../EmploymentAddressFields';
import { MONTHS_OPTIONS } from '../EmploymentMonths/constants';

export interface EmployedSecondaryProps {
  index: number;
  arrayName: string;
}

const EmployedSecondary: React.FunctionComponent<EmployedSecondaryProps> =
  function ({ index, arrayName }) {
    const { error, validation, yearsName, monthsName, yearsOptions } =
      useEmploymentStatusFields({
        type: 'secondary',
        employmentType: 'employed',
        index,
        arrayName,
      });
    const { validation: monthlyIncomeValidation, onBlur } =
      useMonthlyIncomeValidation({
        type: 'secondary',
        employmentType: 'employed',
        index,
        arrayName,
      });
    const baseName = useMemo(
      () => `${arrayName}.${index}.employed_details`,
      [arrayName, index],
    );
    const employmentTypeName = useMemo(
      () => `${baseName}.employed_type`,
      [baseName],
    );
    const monthlyTakeHomeName = useMemo(
      () => `${baseName}.monthly_income_after_tax`,
      [baseName],
    );
    return (
      <div className="grid grid-cols-2 gap-x-3 md:gap-x-4">
        <Field.Validator validation={EMPLOYMENT_TYPE_VALIDATION}>
          <Field.Select
            {...EMPLOYED_PROPS.employedType}
            options={EMPLOYMENT_TYPE_OPTIONS}
            name={employmentTypeName}
          />
        </Field.Validator>
        <Field.Validator validation={monthlyIncomeValidation}>
          <Field.NumberInput
            {...EMPLOYED_PROPS.monthlyIncome}
            name={monthlyTakeHomeName}
            onBlur={onBlur}
          />
        </Field.Validator>
        <Widget.CreditAdditionalEmploymentDetailsEmployedDetailsEmployerNameArrayInput
          {...EMPLOYED_PROPS.employerName}
          className="col-span-2 sm:col-span-1 md:col-span-2 xl:col-span-1"
          index={index}
          name="employed_details.employer_name"
        />
        <Widget.CreditAdditionalEmploymentDetailsEmployedDetailsMainOccupationArrayInput
          {...EMPLOYED_PROPS.mainOccupation}
          className="col-span-2 sm:col-span-1 md:col-span-2 xl:col-span-1"
          index={index}
          name="employed_details.main_occupation"
        />
        <div className="col-span-2">
          <EmploymentAddressFields
            employmentStatus="employed"
            employmentClass="secondary"
            index={index}
            arrayName={arrayName}
          />
        </div>
        <span className="col-span-2 text-12-18-em text-neutral-700 w-full mb-2">
          When did you start your employment?
        </span>
        <Field.Select
          options={MONTHS_OPTIONS}
          name={monthsName}
          placeholder="Month"
        />
        <Field.Select
          options={yearsOptions}
          name={yearsName}
          placeholder="Year"
        />
        {error && <Text text={error} className={ERROR_PROPS.className} />}
        <div className="hidden">
          <Widget.CreditAdditionalEmploymentDetailsEmployedDetailsMonthsEmployedArrayInput
            validation={validation}
            name="employed_details.months_employed"
          />
        </div>
      </div>
    );
  };

export default EmployedSecondary;
